import api, { tokenManager, adminTokenManager, activeWorkspaceManager } from './api';
import type { PlatformRole, WorkspaceRole, WorkspaceTier } from '../types';

export interface AdminUserWorkspace {
  id: string;
  name: string;
  role: WorkspaceRole;
  tier: WorkspaceTier;
  subscriptionStatus: string;
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string | null;
  status: string;
  authProvider: string;
  emailVerified: boolean;
  mfaEnabled: boolean;
  isInternalTester: boolean;
  platformRole: PlatformRole | null;
  lockedUntil: string | null;
  deletionScheduledAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  workspaces: AdminUserWorkspace[];
}

export interface AdminUserDetail extends AdminUser {
  workspaces: Array<
    AdminUserWorkspace & {
      joinedAt: string | null;
      organizations: Array<{ id: string; name: string; timezone: string; sourceCount: number }>;
    }
  >;
  sessions: Array<{
    id: string;
    deviceLabel: string | null;
    ipAddress: string | null;
    createdAt: string;
    lastActiveAt: string;
    expiresAt: string;
    impersonatedByEmail: string | null;
  }>;
}

export interface AdminUserPage {
  users: AdminUser[];
  total: number;
  nextOffset: number | null;
}

export interface AdminUserFilters {
  search?: string;
  status?: string;
  tier?: string;
  authProvider?: string;
  workspaceId?: string;
  staffOnly?: boolean;
  limit?: number;
  offset?: number;
}

export const platformAdminApi = {
  listUsers: async (filters: AdminUserFilters): Promise<AdminUserPage> => {
    const response = await api.get('/api/platform-admin/users', { params: filters });
    return response.data;
  },

  getUser: async (id: string): Promise<AdminUserDetail> => {
    const response = await api.get(`/api/platform-admin/users/${id}`);
    return response.data.user;
  },

  listWorkspaces: async (): Promise<Array<{ id: string; name: string; tier: WorkspaceTier }>> => {
    const response = await api.get('/api/platform-admin/workspaces');
    return response.data.workspaces;
  },

  /**
   * Swap this browser into the target user's identity.
   *
   * Parks the admin's token before overwriting it, drops the active-workspace
   * pin (the customer's own last-active workspace should resolve, not ours),
   * then hard-navigates. The full reload is deliberate — it is the only way to
   * guarantee no react-query cache, SSE stream, or context still holds the
   * admin's data once the identity changes.
   */
  impersonate: async (userId: string, reason?: string): Promise<void> => {
    const adminToken = tokenManager.get();
    if (!adminToken) throw new Error('Not signed in');

    const response = await api.post(`/api/platform-admin/users/${userId}/impersonate`, { reason });

    adminTokenManager.set(adminToken);
    tokenManager.set(response.data.token);
    activeWorkspaceManager.remove();
    window.location.href = '/home';
  },

  /** Revoke the support session and restore the admin's own token. */
  endImpersonation: async (): Promise<void> => {
    const adminToken = adminTokenManager.get();
    try {
      await api.post('/api/platform-admin/impersonation/end', {}, { silenceToast: true });
    } catch {
      // Best-effort: the session may already be expired or revoked. Restoring
      // the admin locally still has to happen, or they are stranded.
    }
    if (adminToken) {
      tokenManager.set(adminToken);
      adminTokenManager.remove();
    } else {
      tokenManager.remove();
    }
    activeWorkspaceManager.remove();
    window.location.href = adminToken ? '/admin/users' : '/login';
  },

  isImpersonating: (): boolean => !!adminTokenManager.get(),
};
